import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Play, Youtube, ExternalLink, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CHANNEL_SHORTS = 'https://www.youtube.com/@truebelieverfam/shorts';
const SUBSCRIBE_URL = 'https://www.youtube.com/@truebelieverfam?sub_confirmation=1';

export const ShortCard = ({ short, index, onSelect, testPrefix = 'short' }) => (
  <motion.button
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    onClick={() => onSelect(short)}
    className="card-dark text-left group"
    data-testid={`${testPrefix}-card-${index}`}
  >
    <div className="relative aspect-[9/16] mb-4 rounded-sm overflow-hidden bg-black">
      <img
        src={`https://i.ytimg.com/vi/${short.video_id}/oar2.jpg`}
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://i.ytimg.com/vi/${short.video_id}/hqdefault.jpg`; }}
        alt={short.title}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
        <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center transition-transform group-hover:scale-110">
          <Play className="text-black ml-1" size={24} />
        </div>
      </div>
      <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm">Short</span>
    </div>
    <h3 className="font-cinzel text-base text-foreground group-hover:text-gold transition-colors line-clamp-2" data-testid={`${testPrefix}-title-${index}`}>
      {short.title}
    </h3>
  </motion.button>
);

export const ShortEmbed = ({ short, testId }) => (
  <iframe
    src={`https://www.youtube.com/embed/${short.video_id}?autoplay=1&rel=0`}
    className="w-full h-full"
    frameBorder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen
    referrerPolicy="strict-origin-when-cross-origin"
    title={short.title}
    data-testid={testId}
  />
);

export const ShortActions = ({ short, className = '', testPrefix = 'short' }) => (
  <div className={`flex flex-wrap gap-3 ${className}`}>
    <a href={`https://www.youtube.com/shorts/${short.video_id}`} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid={`${testPrefix}-open-youtube`}>
      <ExternalLink size={16} /> Watch on YouTube
    </a>
    <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2" data-testid={`${testPrefix}-subscribe`}>
      <Youtube size={16} /> Subscribe
    </a>
  </div>
);

export const useWeeklyShorts = () => {
  const [shorts, setShorts] = useState([]);
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    axios.get(`${API}/media/shorts`)
      .then((res) => { setShorts(res.data.shorts); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);
  return { shorts, status };
};

const ShortPlayer = ({ short, onClose }) => (
  <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-12 flex flex-col md:flex-row gap-8 items-center md:items-start" data-testid="short-player">
    <div className="w-full max-w-[340px] aspect-[9/16] bg-black rounded-sm overflow-hidden border border-gold gold-glow">
      <ShortEmbed short={short} testId="short-player-iframe" />
    </div>
    <div className="flex-1 max-w-lg">
      <p className="text-gold uppercase tracking-[0.3em] text-xs mb-3">Now Playing</p>
      <h3 className="font-cinzel text-2xl text-foreground mb-6" data-testid="short-player-title">{short.title}</h3>
      <ShortActions short={short} className="mb-6" />
      <button onClick={onClose} className="text-gold text-sm uppercase tracking-wider inline-flex items-center gap-2 hover:text-foreground transition-colors" data-testid="short-player-close">
        <X size={14} /> Back to Highlights
      </button>
    </div>
  </motion.div>
);

export const ShortsHighlights = () => {
  const { shorts, status } = useWeeklyShorts();
  const [selected, setSelected] = useState(null);

  return (
    <section className="py-16 bg-card" data-testid="shorts-highlights-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8">
          <div>
            <h2 className="font-cinzel text-2xl text-gold" data-testid="shorts-section-title">Sabbath & Wednesday Night Highlights</h2>
            <p className="text-sm text-muted-foreground mt-2">Fresh picks from our YouTube Shorts, every Sunday.</p>
          </div>
          <a href={CHANNEL_SHORTS} target="_blank" rel="noopener noreferrer" className="text-gold text-sm uppercase tracking-wider inline-flex items-center gap-2 hover:text-foreground transition-colors" data-testid="shorts-view-all">
            View All Shorts <ExternalLink size={14} />
          </a>
        </div>

        {selected && <ShortPlayer short={selected} onClose={() => setSelected(null)} />}

        {status === 'loading' && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="shorts-loading">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[9/16] bg-muted animate-pulse rounded-sm" />)}
          </div>
        )}

        {status === 'error' && (
          <div className="p-8 border border-border rounded-sm text-center" data-testid="shorts-error">
            <p className="text-muted-foreground mb-4">Highlights are taking a short rest. Catch them all on our channel.</p>
            <a href={CHANNEL_SHORTS} target="_blank" rel="noopener noreferrer" className="btn-primary">Watch on YouTube</a>
          </div>
        )}

        {status === 'ready' && !selected && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="shorts-grid">
            {shorts.map((s, i) => <ShortCard key={s.video_id} short={s} index={i} onSelect={setSelected} />)}
          </div>
        )}
      </div>
    </section>
  );
};
